import React, { useState, useEffect } from "react";
import { DialogBox } from "@/components/common/DialogBox";
import RecentVisitsIcon from "@/assets/icons/recent-visits.svg";
import SearchDialogBox from "@/components/common/SearchDialogBox";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { DataTable, Column } from "@/components/common/Table";
import { columns as baseColumns, RecentVisit } from "./recentVisitsColumn";
import DialogDropdown from "@/components/common/DialogDropdown";
import { useDebounce } from "@/hooks/useDebounce";

interface RecentVisitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
}

const RecentVisitsDialog: React.FC<RecentVisitsDialogProps> = ({
  open,
  onOpenChange,
  memberId,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 1000); // Debounce search value

  const [allVisits, setAllVisits] = useState<RecentVisit[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [page, setPage] = useState(1);
  const [, setTotalPages] = useState(1);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [timeRange, setTimeRange] = useState("all");

  const rowsPerPage = 20;

  const timeRangeOptions = [
    { label: "All Time", value: "all" },
    { label: "1 Month", value: "30" },
    { label: "2 Months", value: "60" },
    { label: "3 Months", value: "90" },
    { label: "4 Months", value: "120" },
    { label: "5 Months", value: "150" },
    { label: "6 Months", value: "180" },
    { label: "7 Months", value: "210" },
    { label: "8 Months", value: "240" },
    { label: "9 Months", value: "270" },
    { label: "10 Months", value: "300" },
    { label: "11 Months", value: "330" },
    { label: "12 Months", value: "365" },
  ];

  const dialogColumns: Column<RecentVisit>[] = baseColumns;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTimeRangeChange = (data: any) => {
    setTimeRange(data.days);

    if (data.days === "all") {
      setStartDate(undefined);
      setEndDate(undefined);
    } else {
      setStartDate(data.startDate);
      setEndDate(data.endDate);
    }
  };

  const fetchVisits = async (
    pageNumber: number = 1,
    append: boolean = false
  ) => { // NOSONAR
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
        setAllVisits([]);
        setPage(1);
        setHasMoreData(true);
      }

      const url = routes.cards.recentVisits(memberId);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestBody: any = {
        view: "list",
        searchKey: debouncedSearchValue.trim() || undefined, // Use debounced value
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        days: timeRange !== "all" ? timeRange : undefined,
        page: pageNumber,
        limit: rowsPerPage,
      };

      Object.keys(requestBody).forEach(
        (key) => requestBody[key] === undefined && delete requestBody[key]
      );

      const res = await api.post(url, requestBody);

      let rawData = [];
      let paginationInfo = null;

      if (res.data?.data && Array.isArray(res.data.data)) {
        rawData = res.data.data;
        paginationInfo = res.data.pagination;
      } else if (Array.isArray(res.data)) {
        rawData = res.data;
      } else if (res.data) {
        rawData = [res.data];
      }

      const transformedData: RecentVisit[] = rawData.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any, index: number) => ({
          id: (pageNumber - 1) * rowsPerPage + index + 1,
          date: item.date || item.visitDate || item.encounterDate || "-",
          type: item.type || item.visitType || item.encounterType || "-",
          facility:
            item.facility ||
            item.facilityName ||
            item.facility_name ||
            item.location ||
            "-",
          diagnosis:
            item.diagnosis || item.diagnoses || item.primaryDiagnosis || null,
        })
      );

      if (append) {
        setAllVisits((prev) => [...prev, ...transformedData]);
      } else {
        setAllVisits(transformedData);
      }

      if (paginationInfo) {
        setTotalPages(paginationInfo.totalPages || 1);
        setHasMoreData(pageNumber < paginationInfo.totalPages);
      } else {
        setHasMoreData(transformedData.length === rowsPerPage);
      }

      setPage(pageNumber);
    } catch (err) {
      console.error("API Error fetching recent visits:", err);
      // const error = err as { response?: { data?: { message?: string } }; message?: string };
      // const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch recent visits.";
      // setError(errorMessage);
      setHasMoreData(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreData = async () => {
    if (!hasMoreData || isLoadingMore) return;
    const nextPage = page + 1;
    await fetchVisits(nextPage, true);
  };

  const handleScroll = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => {
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
    if (isNearBottom && hasMoreData && !isLoadingMore && !loading) {
      loadMoreData();
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setTimeRange("all");
      setStartDate(undefined);
      setEndDate(undefined);
      setAllVisits([]);
      setPage(1);
      setHasMoreData(true);
      setTotalPages(1);
    }
  }, [open]);

  // Combined useEffect - triggers on ALL filter changes including debounced search
  useEffect(() => {
    if (!open) return;
    fetchVisits(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, timeRange, startDate, endDate, debouncedSearchValue]);

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      icon={RecentVisitsIcon}
      iconAlt='Recent Visits'
      title='Recent Visits / Encounters'>
      <div className='min-h-[500px]'>
        <div className='mb-4 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <SearchDialogBox
              value={searchValue}
              onChange={setSearchValue}
              placeholder='Search by Facility Name'
              width='368px'
            />

            <div style={{ width: "180px", height: "40px" }}>
              <DialogDropdown
                options={timeRangeOptions}
                value={timeRange}
                onChange={handleTimeRangeChange}
                icon='calendar-line'
              />
            </div>
          </div>

          <div className='border rounded-lg'>
            <DataTable<RecentVisit>
              data={{ data: allVisits, total: allVisits.length }}
              columns={dialogColumns}
              height={allVisits.length === 0 ? "440px" : "auto"}
              maxHeight={"440px"}
              isLoading={loading}
              isLoadingMore={isLoadingMore}
              hasMoreData={hasMoreData}
              allData={allVisits}
              onScroll={handleScroll}
              skeletonCount={10}
            />
          </div>
        </div>
      </div>
    </DialogBox>
  );
};

export default RecentVisitsDialog;
